/**
 * Проверка и арифметика формы ордера.
 *
 * Зеркало services/internal/webapi/validate.go — ТЕМИ ЖЕ формулировками: две
 * проверки одного правила, сказанные по-разному, разъезжаются в первый же день,
 * и оператор перестаёт понимать, кому верить.
 *
 * Модуль чистый: ни сети, ни React, ни времени изнутри. Поэтому он покрыт
 * тестами целиком, а компонент формы остаётся тонким.
 *
 * ВАЖНО про деньги: всё считается целочисленно через lib/money. Number(price)
 * запрещён здесь по той же причине, по которой запрещён везде, — цена, не
 * кратная шагу, отвергается биржей целиком.
 */

import { cmp, format, mul, parse, type Micro, SCALE } from "./money";
import type { OrderType, Side } from "../api/types";

/**
 * Запас, который биржа вычитает из срока GTD-ордера, и минимальная жизнь после
 * него. Зеркало executor.GTDSafetyMargin и GTDMinLifetime.
 */
export const GTD_SAFETY_SEC = 60;
export const GTD_MIN_LIFETIME_SEC = 10;

/** Черновик ордера в том виде, в каком его держит форма. */
export interface OrderDraft {
  side: Side;
  /** Как ввёл оператор, без нормализации: ошибку показываем на его строке. */
  price: string;
  size: string;
  orderType: OrderType;
  /**
   * Срок GTD задаётся ДЛИТЕЛЬНОСТЬЮ, а не моментом. Пикер даты на рынке,
   * который живёт пять минут, неудобен, а руками набранное абсолютное время с
   * большой вероятностью уже мертво к моменту отправки. null у остальных типов.
   */
  lifetimeSec: number | null;
}

/** Ограничения рынка, против которых проверяется черновик. */
export interface OrderConstraints {
  /**
   * Шаг СТАКАНА, а не справочника: tick_size_change доходит до стакана раньше,
   * чем до таблицы markets, и по устаревшему шагу форма отвергала бы верную
   * цену и пропускала неверную.
   */
  tickSize: string;
  minOrderSize: string;
  /** false, если рынок закрыт или неактивен. */
  tradable: boolean;
}

export type ProblemField = "price" | "size" | "lifetime" | "market";

export interface Problem {
  field: ProblemField;
  message: string;
  /**
   * Поле просто ещё не заполнено, а не заполнено НЕВЕРНО.
   *
   * Различие нужно интерфейсу: отправку такая проблема блокирует наравне с
   * остальными, но показывать «цена не разобрана» человеку, который ещё ничего
   * не набрал, — значит встречать его двумя красными строками на пустой форме и
   * приучать не читать их вовсе.
   */
  blank?: boolean;
}

/**
 * Проверяет черновик и возвращает ВСЕ найденные проблемы, а не первую.
 *
 * В отличие от сервера, который отвечает одним отказом: там ответ один на
 * запрос, а здесь оператор видит форму целиком, и подсвечивать ошибки по одной
 * значит заставлять его исправлять их по кругу.
 */
export function validateDraft(d: OrderDraft, c: OrderConstraints): Problem[] {
  const problems: Problem[] = [];

  if (!c.tradable) {
    problems.push({ field: "market", message: "рынок закрыт — биржа не примет по нему ордер" });
  }

  const tick = parse(c.tickSize);
  const price = parse(d.price);
  if (d.price.trim() === "") {
    problems.push({ field: "price", message: "цена не задана", blank: true });
  } else if (price === null) {
    problems.push({ field: "price", message: "цена не разобрана" });
  } else if (price <= 0) {
    problems.push({ field: "price", message: "цена должна быть строго больше 0" });
  } else if (price >= SCALE) {
    problems.push({ field: "price", message: "цена должна быть строго меньше 1" });
  } else if (tick === null || tick <= 0) {
    problems.push({ field: "price", message: "шаг цены рынка неизвестен" });
  } else if (price % tick !== 0) {
    const lo = price - (price % tick);
    problems.push({
      field: "price",
      message: `цена не кратна шагу ${format(tick)} — ближайшие допустимые: ${format(lo)} и ${format(lo + tick)}`,
    });
  }

  const size = parse(d.size);
  const minSize = parse(c.minOrderSize);
  if (d.size.trim() === "") {
    problems.push({ field: "size", message: "размер не задан", blank: true });
  } else if (size === null) {
    problems.push({ field: "size", message: "размер не разобран" });
  } else if (size <= 0) {
    problems.push({ field: "size", message: "размер должен быть больше нуля" });
  } else if (minSize !== null && minSize > 0 && cmp(size, minSize) < 0) {
    problems.push({
      field: "size",
      message: `размер меньше минимального для этого рынка (${format(minSize)})`,
    });
  }

  if (price !== null && size !== null && price > 0 && size > 0 && mul(price, size) === null) {
    problems.push({ field: "size", message: "цена × размер не помещается в допустимый диапазон" });
  }

  if (d.orderType === "GTD") {
    const min = GTD_SAFETY_SEC + GTD_MIN_LIFETIME_SEC;
    if (d.lifetimeSec === null || d.lifetimeSec < min) {
      problems.push({
        field: "lifetime",
        message: `биржа гасит GTD-ордер на ${GTD_SAFETY_SEC} с раньше заявленного времени, поэтому срок жизни меньше ${min} с бессмысленен`,
      });
    }
  }

  return problems;
}

/**
 * Нотионал в USD: цена ∈ (0,1) × размер в share.
 * null, если что-то не разобрано, — показывать «NaN USD» на экране, откуда
 * тратят деньги, нельзя.
 */
export function notional(price: string, size: string): string | null {
  const p = parse(price);
  const s = parse(size);
  if (p === null || s === null) return null;
  const n = mul(p, s);
  return n === null ? null : format(n);
}

/**
 * Шаг цены на один тик. Number(price) + 0.01 здесь запрещён по той же причине,
 * по которой запрещён везде.
 *
 * Результат зажимается в открытый интервал (0, 1): цена ровно 0 или 1 — не
 * «почти ноль» и не «почти сто процентов», а ордер, который биржа не примет.
 */
export function stepPrice(price: string, tick: string, dir: 1 | -1): string {
  const p = parse(price);
  const t = parse(tick);
  if (p === null || t === null || t <= 0) return price;

  // Сначала прижимаем к сетке, потом шагаем: иначе шаг от «0.523» дал бы
  // «0.533», то есть перенёс бы промах оператора в результат.
  const snapped = p - ((p % t) + t) % t;
  let next = dir > 0 ? snapped + t : snapped === p ? snapped - t : snapped;
  if (next <= 0) next = t;
  if (next >= SCALE) next = SCALE - t;
  return format(next);
}

/**
 * Прижатие цены к сетке шага. Вызывается при потере фокуса: оператор печатает
 * быстро и промахивается, а отказ биржи по некратной цене стоит целого ордера.
 */
export function snapPrice(price: string, tick: string): string {
  const p = parse(price);
  const t = parse(tick);
  if (p === null || t === null || t <= 0 || p <= 0) return price;

  const rem = p % t;
  // Ближайшее кратное, а не вниз: оператор целился в введённое значение, и
  // «0.529» при шаге 0.01 он имел в виду как 0.53, а не как 0.52.
  let snapped = rem * 2 >= t ? p - rem + t : p - rem;
  if (snapped <= 0) snapped = t;
  if (snapped >= SCALE) snapped = SCALE - t;
  return format(snapped);
}

/**
 * Пресеты срока жизни GTD.
 *
 * Начинаются с двух минут не случайно: биржа вычитает минуту, поэтому «1 мин»
 * дала бы ордер, живущий ноль секунд.
 */
export const GTD_PRESETS: ReadonlyArray<{ label: string; sec: number }> = [
  { label: "2 мин", sec: 120 },
  { label: "5 мин", sec: 300 },
  { label: "15 мин", sec: 900 },
];

/**
 * Что означает каждый тип ордера — словами оператора.
 *
 * Таблица здесь, а не в компоненте, по той же причине, по которой здесь живут
 * правила валидации: у неё ДВА потребителя (подсказка при наведении и
 * раскрывающаяся справка), и разойдись они — оператор получил бы два описания
 * одного типа, сказанных по-разному, и перестал бы верить обоим.
 *
 * Четыре буквы на кнопках непрозрачны by design: это биржевые сокращения, и
 * переименовывать их в «висит до снятия» нельзя — в списке ордеров, в логах и в
 * ответах биржи стоят ровно GTC/GTD/FOK/FAK, и оператор обязан узнавать их. Но
 * молчать о смысле тоже нельзя: выбор типа решает, заплатит он спред или
 * получит, и исполнится ордер сейчас или будет ждать.
 *
 * Роль названа первой, потому что она — главное решение: мейкер встаёт в стакан
 * и ждёт, тейкер бьёт по стакану немедленно. Совпадает с ролью в подписях
 * панели быстрых действий (Maker L1 / Taker), и это не совпадение: одно понятие
 * обязано называться одинаково на всех экранах.
 */
export interface OrderTypeInfo {
  type: OrderType;
  role: "мейкер" | "тейкер";
  /** Одна строка: что произойдёт после отправки. */
  summary: string;
  /** Чем этот тип отличается от соседнего той же роли. */
  detail: string;
}

export const ORDER_TYPES: ReadonlyArray<OrderTypeInfo> = [
  {
    type: "GTC",
    role: "мейкер",
    summary: "встаёт в стакан и висит, пока не исполнится или не снимут",
    detail:
      "Спред не платится, а получается. Срока нет: ордер переживает и смену цены, " +
      "и уход со страницы — снимать его придётся руками либо «снять все по рынку».",
  },
  {
    type: "GTD",
    role: "мейкер",
    summary: "то же, что GTC, но сам гаснет к указанному сроку",
    detail:
      "Тот же мейкер, но с таймером: на пятиминутном рынке ордер, переживший окно, " +
      "уже не нужен. Биржа гасит GTD на минуту раньше заявленного, и пресеты это учитывают.",
  },
  {
    type: "FOK",
    role: "тейкер",
    summary: "исполнить ЦЕЛИКОМ немедленно или отменить полностью",
    detail:
      "Всё или ничего. Против агрегированного уровня, размер которого всё время меняется, " +
      "это чаще всего «ничего»: не хватило объёма — не исполнилось нисколько, и почему, " +
      "биржа не объяснит. Поэтому быстрые действия используют FAK, а не FOK.",
  },
  {
    type: "FAK",
    role: "тейкер",
    summary: "исполнить сколько получится немедленно, остаток отменить",
    detail:
      "Частичное исполнение вместо отказа: взял, сколько было на уровне, остальное снял. " +
      "Именно этот тип уходит с кнопки Taker. Если встречных заявок не нашлось вовсе, " +
      "биржа отвечает «no orders found to match» — это не поломка, а ушедшая цена.",
  },
];

/** Описание типа. Возвращает undefined только для значения вне контракта. */
export function orderTypeInfo(t: OrderType): OrderTypeInfo | undefined {
  return ORDER_TYPES.find((i) => i.type === t);
}

/**
 * Короткая метка подсистемы, выставившей ордер.
 *
 * Короткая, потому что стоит в строке таблицы рядом со временем: «automaker» там
 * занимает столько же места, сколько само время. Незнакомое имя показывается как
 * есть — выдумывать перевод для подсистемы, которой ещё нет, нельзя.
 *
 * Здесь, а не в таблице ордеров: ту же метку печатает подсказка точки ордера на
 * графике цены, и две копии перевода разошлись бы на первой новой подсистеме.
 */
export function issuerLabel(issuedBy: string): string {
  switch (issuedBy) {
    case "automaker":
      return "авто 0.99";
    case "follow":
      return "копирование";
    default:
      return issuedBy;
  }
}

/** Микро-единицы для сравнения уровней стакана с нашими ордерами. */
export type { Micro };
